import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const CTASection = styled.section`
  padding: 5rem 2rem;
  text-align: center;
  background: ${({ theme }) => theme.colors.cardBackground || theme.colors.background};

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.headline};
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.activeColor};
`;

const Description = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.paragraph};
  margin-bottom: 2.5rem;
  line-height: 1.7;
`;

const CTAButton = styled.button`
  background: ${({ theme }) => theme.colors.activeColor};
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px ${({ theme }) => theme.colors.activeColor}40;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px ${({ theme }) => theme.colors.activeColor}50;
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const Disclaimer = styled.p`
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.paragraph}99;
`;

const FinalCTA = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const fallback = setTimeout(() => setIsVisible(true), 3500);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <CTASection ref={sectionRef}>
      <Container className={isVisible ? 'visible' : ''}>
        <SectionLabel>Get Started</SectionLabel>
        <Title>
          5 Analyses a Month. <AccentText>No Credit Card. No Catch.</AccentText>
        </Title>
        <Description>
          Pick a ZIP code. See which listings actually cash flow. If you need more than
          5 runs a month, Starter is $9.99 for 20.
        </Description>
        <CTAButton onClick={() => navigate('/signup')}>
          Start Analyzing Free
        </CTAButton>
        <Disclaimer>
          The free tier doesn't expire. Upgrade only if you want to.
        </Disclaimer>
      </Container>
    </CTASection>
  );
};

export default FinalCTA;
